import { z } from 'zod';
import superjson from 'superjson';
import { Selectable } from 'kysely';
import { PublishedSites } from '../../helpers/schema';

export const schema = z.object({
  sessionId: z.string().min(1),
  subdomain: z
    .string()
    .min(3, 'Subdomain must be at least 3 characters')
    .max(63, 'Subdomain must be at most 63 characters')
    .regex(
      /^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]$/,
      'Subdomain must be lowercase alphanumeric, hyphens allowed but not at start/end',
    ),
  customDomain: z
    .string()
    .regex(
      /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/i,
      'Must be a valid domain (e.g., myapp.example.com)',
    )
    .optional(),
  files: z
    .array(
      z.object({
        path: z.string(),
        content: z.string(),
      }),
    )
    .min(1, 'At least one file is required'),
  pythonRuntime: z.enum(['worker', 'pyodide']).optional(),
});

export type InputType = z.infer<typeof schema>;

export type OutputType = {
  site: Selectable<PublishedSites>;
};

export const publishSite = async (body: InputType, init?: RequestInit): Promise<OutputType> => {
  const validatedInput = schema.parse(body);
  const requestBody = superjson.stringify(validatedInput);

  const result = await fetch(`/_api/sites/publish`, {
    ...init,
    method: 'POST',
    credentials: 'include',
    body: requestBody,
    headers: {
      'Content-Type': 'application/json',
      ...(init?.headers ?? {}),
    },
  });

  const responseText = await result.text();

  if (!result.ok) {
    // Try to parse as superjson error, fallback to raw text
    try {
      const errorObject = superjson.parse<{ error: string }>(responseText);
      throw new Error(errorObject.error);
    } catch {
      throw new Error(responseText || `Request failed with status ${result.status}`);
    }
  }

  return superjson.parse<OutputType>(responseText);
};
